'use client';
import { useEffect, type ReactNode } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { Blocks, Settings2 } from 'lucide-react';
import { ModuleShell } from '../../components/shell/ModuleShell';
import { WorkspacePage } from '../../components/ui/WorkspacePrimitives';
import { WorkspaceShell } from '../../components/ui/WorkspaceShell';
import { ControlSurfaceDocument, ControlSurfaceState } from '../../components/ui/ControlSurface';
import { ModuleHeader } from '../../components/ui/ModuleHeader';
import { MotionReveal } from '../../components/ui/Motion';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/states';
import { pluginLucideIcon } from '../../lib/pluginIcons';
import { PluginErrorBoundary, PluginPlaceholder as Placeholder } from '../../components/plugin/PluginUiGuards';
import { usePluginUi, useMe } from '../../lib/queries';
import { useTranslation } from '../../lib/i18n';
import { isPluginUserPage, pluginDetailSectionHref, resolvePluginSettingsRoute } from '../../lib/pluginNav';
import { usePagePath } from '../../lib/usePagePath';
import { isPluginOverlayRoute } from '../../lib/sameDocumentNavigation';
import {
  loadPluginUi, matchPluginPage, usePluginUiRegistration,
} from '../../lib/pluginUi';

/** One plugin page body, shared by the ordinary page route and the host-owned page overlay. Exactly one
 *  instance becomes active from the manifest's `web.presentation`; the inactive route renders and loads
 *  nothing, so a modal plugin never paints a normal page behind its overlay. */
export function PluginHostView({ presentation, route }: {
  presentation: 'page' | 'overlay';
  route?: { plugin: string; rest: string[] };
}) {
  const params = useParams<{ plugin: string; rest?: string[] }>();
  const plugin = route?.plugin ?? params.plugin;
  const router = useRouter();
  const { t, locale } = useTranslation();
  const listing = usePluginUi(locale);
  // The sub-address comes from the path of the page that is MOUNTED, not from the route params: a
  // same-plugin move rewrites the address without routing (`navigateWithinPlugin`), which Next's params
  // would not follow. `usePagePath` keeps the last page path while an overlay owns the address (Settings,
  // a plugin overlay), so the page underneath does not read the overlay's path as its own sub-address.
  // Segments stay URL-encoded, exactly as Next's `params.rest` delivers them (and as `PageOverlayHost` passes them).
  const pagePath = usePagePath(listing.data);
  const ownPath = pagePath !== null && (pagePath === `/p/${plugin}` || pagePath.startsWith(`/p/${plugin}/`)) ? pagePath : null;
  const rest = route?.rest ?? (ownPath === null ? params.rest ?? [] : ownPath.split('/').filter(Boolean).slice(2));
  const pageRoute = rest.join('/');
  const mainEntry = listing.data?.find(p => p.name === plugin);
  // Main-bundle sections belong to the listing, whether or not each appears in navigation. Only a
  // reserved user page (or an account without the main listing) needs the server's exact-page grant.
  const discoverUserPage = rest.length > 0 && (!mainEntry || isPluginUserPage(mainEntry, pageRoute));
  const targeted = usePluginUi(locale, { plugin, page: pageRoute }, discoverUserPage);
  const loading = listing.isLoading || (discoverUserPage && targeted.isLoading);
  // The listing can omit a disabled plugin or one this account has not been granted.
  // An administrator can open settings to check the installation and their own grant.
  const isAdmin = useMe().data?.user?.is_admin === true;
  const userPage = discoverUserPage && !targeted.isError ? targeted.data?.find(p => p.name === plugin && p.page === pageRoute) : undefined;
  // An empty or rejected exact-page response must never fall through to the main bundle.
  const entry = discoverUserPage ? userPage : mainEntry;
  const overlay = isPluginOverlayRoute(`/p/${plugin}${pageRoute ? `/${pageRoute}` : ''}`, listing.data);
  const active = !loading && (presentation === 'overlay') === overlay;

  const { compatible, registration } = usePluginUiRegistration(active ? entry : undefined, loadPluginUi);
  const { explicitlyRequestedSetting, bareSoleSetting } = resolvePluginSettingsRoute(entry, rest);
  const registeredPage = registration ? userPage
    ? registration.pages?.[pageRoute] ? { Component: registration.pages[pageRoute], params: {} } : undefined
    : matchPluginPage(registration.pages, rest) : undefined;
  const internalDetailSection = explicitlyRequestedSetting?.placement === 'pluginDetail'
    ? explicitlyRequestedSetting
    : registration && !registeredPage && bareSoleSetting?.placement === 'pluginDetail'
      ? bareSoleSetting
      : undefined;
  useEffect(() => {
    if (active && internalDetailSection) router.replace(pluginDetailSectionHref(plugin, internalDetailSection.id));
  }, [active, internalDetailSection, plugin, router]);

  const strings = t.pluginUi;
  // A plugin that is off is a page the operator can still land on — from a bookmark, from the address
  // bar, or from a link that predates the switch. It gets the chrome of a page (masthead, tab title)
  // and the one action that resolves it, instead of a bare sentence on an empty screen.
  const noticeState = (text: string, withAction = false) => (
    <ControlSurfaceDocument>
      <ControlSurfaceState>
        <EmptyState
          title={strings.unavailableTitle}
          description={text}
          icon={Blocks}
          action={withAction
            ? <Button variant="accent" icon={Settings2} onClick={() => router.push('/settings?cat=plugins')}>{strings.manage}</Button>
            : undefined}
        />
      </ControlSurfaceState>
    </ControlSurfaceDocument>
  );
  const notice = (text: string, withAction = false) => presentation === 'overlay'
    ? noticeState(text, withAction)
    : (
      <>
        {/* Draws nothing: it publishes the page name for the shell masthead and the browser tab. */}
        <ModuleHeader title={entry?.label ?? plugin} icon={Blocks} />
        <WorkspaceShell
          variant="single"
          hero={{ eyebrow: strings.eyebrow, title: entry?.label ?? plugin, icon: Blocks, mascot: 'error' }}
        >
          {noticeState(text, withAction)}
        </WorkspaceShell>
      </>
    );

  let body: ReactNode;
  if (loading) body = null;
  else if (discoverUserPage && targeted.isError) body = notice(strings.loadFailed);
  else if (!entry) body = notice(strings.notGranted, isAdmin);
  else if (!compatible) body = notice(strings.incompatible);
  else if (registration === undefined) body = null; // bundle loading
  else if (registration === null) body = notice(strings.loadFailed, true);
  else if (internalDetailSection) body = null; // redirected into Settings → Plugins by the effect above
  else {
    const page = registeredPage;
    // `/p/<plugin>` also resolves to the only settings section of a plugin that has nothing else, so its
    // address does not have to repeat its own name (`/p/skills/settings/skills`). Pages still win the bare
    // route, and `settings/<id>` keeps working for every plugin and every existing link.
    const sole = !page && bareSoleSetting?.placement !== 'pluginDetail' ? bareSoleSetting : undefined;
    const section = explicitlyRequestedSetting?.placement !== 'pluginDetail'
      ? explicitlyRequestedSetting ?? sole
      : sole;
    const settingsComponent = section ? registration.settings?.[section.id] : undefined;
    const match = settingsComponent && section
      ? { Component: settingsComponent, params: { id: section.id } }
      : page;
    // A declared section whose bundle registered no component is a different miss from an unknown page:
    // the menu offered settings by name, so saying "page missing" would answer a question nobody asked.
    const rendered = match
      ? <match.Component plugin={plugin} params={match.params} rest={rest} surface={presentation === 'overlay' ? 'deck' : 'page'} />
      : <Placeholder text={section ? strings.settingsUnavailable : strings.pageMissing} />;
    // A section that draws its own page shell has already been given the page column by that shell, so
    // wrapping it again nests two page frames: the gutter and the bottom padding apply twice and the
    // page comes out narrower than every sibling register. Only the bundle knows what its component
    // renders, which is why it declares the ids it frames itself.
    const ownsFrame = section !== undefined && registration.ownsPageFrame?.includes(section.id) === true;
    // A settings section is written for the Settings deck: the deck's panel supplies the document surface
    // its groups sit on, and the deck page supplies the masthead title and the page column. Reached
    // directly it has to bring all three, or it reads as a fragment pasted onto an empty screen.
    body = presentation === 'overlay' ? rendered : settingsComponent && section && !ownsFrame ? (
      <WorkspacePage>
        {/* The masthead and the browser tab are the host's to name; everything visible belongs to the
            section, which on a page heads itself with components.PluginPageHeader above its own
            document surface — the header has to sit ABOVE that surface, so the host cannot supply it
            from out here. In the Settings deck the panel keeps supplying both. */}
        <ModuleHeader title={section.label} icon={pluginLucideIcon(section.icon)} />
        {/* The class is structural, not decorative: it tells the page shell that this entrance is the
            one wrapper standing between the page and its hero, so the hero still gets the shell's
            top breathing room (app/styles/components/workspace-shell.css). */}
        <MotionReveal className="workspace-page__lead">{rendered}</MotionReveal>
      </WorkspacePage>
    ) : settingsComponent && section ? (
      <>
        {/* Nothing but the page name: the header renders no DOM without children, so the masthead and
            the browser tab keep reading the section's label while the section owns every pixel. The host
            has no header here and no save channel: a section reports its own outcomes through the toast
            seam (`useAutosaveToast`, `useToast`). */}
        <ModuleHeader title={section.label} icon={pluginLucideIcon(section.icon)} />
        {rendered}
      </>
    ) : rendered;
  }

  if (!active) return null;
  const guarded = <PluginErrorBoundary notice={strings.crashed}>{body}</PluginErrorBoundary>;
  return presentation === 'overlay'
    ? guarded
    : <ModuleShell moduleId={`plugin-${plugin}`}>{guarded}</ModuleShell>;
}
